import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { PerfMarkerFactory } from '@bingads-webui/performance-marker';

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

/**
 *
 * @param {React.Component} WrappedComponent
 * @param {?object} fallback
 *  Fallback options resolve in the case where user of
 *  withPerfMarker does not explicitly pass prop of perfMarker
 *  in the following order
 *    1. fallback.perfMarker -- expecting perf marker object
 *    2. fallback.name -- creates new perf marker with name
 *
 *  In the case that no fallback is supplied and the mechanism
 *  is being relied upon the wrapper will throw an error.
 */

const withPerfMarkerInternal = function (WrappedComponent, fallback) {
  class WithPerfMarker extends React.Component {
    constructor(props) {
      super(props);

      this.perfMarker = props.perfMarker;
      if (!this.perfMarker) {
        if (_.has(fallback, 'perfMarker') && PerfMarkerFactory.isPerfMarker(fallback.perfMarker)) {
          this.perfMarker = fallback.perfMarker;
        } else if (_.has(fallback, 'name') && _.isString(fallback.name)) {
          this.perfMarker = PerfMarkerFactory.createMarker(fallback.name, fallback.options);
        } else {
          throw new SyntaxError('No perfmarker supplied as prop or (no|incorrect) fallback provided');
        }
      }

      this.perfMarker.willInit();
    }

    componentDidMount() {
      if (!this.perfMarker.autoEnd) {
        this.perfMarker.done();
      }
    }

    render() {
      this.perfMarker.willRender();

      return (<WrappedComponent
        {...this.props}
        ref={this.props.forwardedRef}
        perfMarker={this.perfMarker}
      />);
    }
  }

  WithPerfMarker.propTypes = {
    perfMarker: PropTypes.shape({
      willFetchFile: PropTypes.func.isRequired,
      willInit: PropTypes.func.isRequired,
      willFetchData: PropTypes.func.isRequired,
      willRender: PropTypes.func.isRequired,
      done: PropTypes.func.isRequired,
      createChild: PropTypes.func.isRequired,
    }),
    forwardedRef: PropTypes.objectOf(PropTypes.any),
  };

  WithPerfMarker.defaultProps = {
    perfMarker: null,
    forwardedRef: null,
  };

  return WithPerfMarker;
};

const withPerfMarker = function (WrappedComponent, fallback) {
  const Wrapped = withPerfMarkerInternal(WrappedComponent, fallback);
  const WrappedWithForwardRef = React.forwardRef((props, ref) =>
    <Wrapped {...props} forwardedRef={ref} />);
  WrappedWithForwardRef.displayName = `WithPerfMarker(${getDisplayName(WrappedComponent)})`;
  return WrappedWithForwardRef;
};

export { withPerfMarker, withPerfMarkerInternal };
